import React, { useState, useEffect, useMemo } from 'react';
import { X, Calendar as CalendarIcon, Eye, Globe, Zap } from 'lucide-react';
import { getCalendars } from '../api';
import { Badge } from './catalyst/badge';
import { Button } from './catalyst/button';
import { Checkbox } from './catalyst/checkbox';
import { Dialog, DialogBody, DialogTitle } from './catalyst/dialog';
import { Divider } from './catalyst/divider';
import { Subheading } from './catalyst/heading';
import { Input } from './catalyst/input';
import { Switch } from './catalyst/switch';
import { Text } from './catalyst/text';

const COMMON_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Toronto',
  'America/Vancouver',
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  'America/Mexico_City',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Amsterdam',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Zurich',
  'Europe/Stockholm',
  'Europe/Warsaw',
  'Europe/Moscow',
  'Europe/Istanbul',
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Africa/Lagos',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Colombo',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Shanghai',
  'Asia/Taipei',
  'Asia/Seoul',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Perth',
  'Pacific/Auckland',
  'Pacific/Fiji',
];

function getTimezoneLabel(tz) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date());
    const abbr = parts.find(p => p.type === 'timeZoneName')?.value || '';
    const city = tz.split('/').pop().replace(/_/g, ' ');
    return `${city} (${abbr})`;
  } catch {
    return tz;
  }
}

export default function SettingsModal({
  onClose,
  hiddenCalendars,
  onToggleCalendar,
  dimKeywords,
  onDimKeywordsChange,
  secondaryTimezone,
  onSecondaryTimezoneChange,
  quickCreateEnabled,
  onQuickCreateEnabledChange,
  googleConnected = false,
  googleUser,
  onConnectGoogle,
  onDisconnectGoogle
}) {
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKeyword, setNewKeyword] = useState('');
  const [tzSearch, setTzSearch] = useState('');
  const [googleActionLoading, setGoogleActionLoading] = useState(false);

  useEffect(() => {
    if (googleConnected) {
      fetchCalendars();
    } else {
      setCalendars([]);
      setLoading(false);
    }
  }, [googleConnected]);

  const fetchCalendars = async () => {
    try {
      setLoading(true);
      const data = await getCalendars();
      setCalendars(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching calendars:', error);
      setLoading(false);
    }
  };

  const handleConnectGoogle = async () => {
    if (!onConnectGoogle || googleActionLoading) return;
    setGoogleActionLoading(true);
    try {
      await onConnectGoogle();
    } catch (error) {
      console.error('Error connecting Google:', error);
      setGoogleActionLoading(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    if (!onDisconnectGoogle || googleActionLoading) return;
    setGoogleActionLoading(true);
    try {
      await onDisconnectGoogle();
      setCalendars([]);
    } catch (error) {
      console.error('Error disconnecting Google:', error);
    } finally {
      setGoogleActionLoading(false);
    }
  };

  const handleToggleCalendar = (calendarId) => {
    onToggleCalendar(calendarId);
  };

  const handleAddKeyword = () => {
    const kw = newKeyword.trim();
    if (kw && !dimKeywords.includes(kw)) {
      onDimKeywordsChange([...dimKeywords, kw]);
      setNewKeyword('');
    }
  };

  const handleRemoveKeyword = (keyword) => {
    onDimKeywordsChange(dimKeywords.filter(k => k !== keyword));
  };

  const filteredTimezones = useMemo(() => {
    if (!tzSearch.trim()) return COMMON_TIMEZONES;
    const q = tzSearch.toLowerCase();
    return COMMON_TIMEZONES.filter(tz => {
      const label = getTimezoneLabel(tz).toLowerCase();
      const raw = tz.toLowerCase();
      return label.includes(q) || raw.includes(q);
    });
  }, [tzSearch]);

  return (
    <Dialog size="2xl" onClose={onClose} className="max-h-[90vh] overflow-hidden p-0">
      <div className="flex max-h-[90vh] flex-col">
        <div className="flex items-center justify-between border-b border-zinc-200 px-6 py-4">
          <div>
            <DialogTitle>Settings</DialogTitle>
            <Text className="mt-1 truncate">
              {googleConnected ? (googleUser?.email || googleUser?.name || 'Google connected') : 'Google is not connected'}
            </Text>
          </div>
          <Button plain onClick={onClose} className="h-9 w-9 rounded-full p-0" aria-label="Close settings">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <DialogBody className="mt-0 flex-1 space-y-8 overflow-y-auto p-6">
          <section className="space-y-3">
            <Subheading className="flex items-center gap-2 text-base">
              <CalendarIcon className="h-4 w-4 text-zinc-500" />
              Google Calendar
            </Subheading>

            <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-zinc-950">
                      {googleConnected ? 'Connected' : 'Not connected'}
                    </span>
                    <Badge color={googleConnected ? 'emerald' : 'amber'}>
                      {googleConnected ? 'Active' : 'Setup'}
                    </Badge>
                  </div>
                  <Text className="mt-1 truncate">
                    {googleConnected
                      ? (googleUser?.email || googleUser?.name || 'Google account connected')
                      : 'Calendar, events, and tasks are unavailable.'}
                  </Text>
                </div>

                {googleConnected ? (
                  <Button
                    outline
                    color="red"
                    onClick={handleDisconnectGoogle}
                    disabled={googleActionLoading}
                  >
                    Disconnect
                  </Button>
                ) : (
                  <Button
                    color="dark/zinc"
                    onClick={handleConnectGoogle}
                    disabled={googleActionLoading}
                  >
                    {googleActionLoading ? 'Connecting...' : 'Connect'}
                  </Button>
                )}
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <Subheading className="flex items-center gap-2 text-base">
              <Globe className="h-4 w-4 text-zinc-500" />
              Secondary Timezone
            </Subheading>

            {secondaryTimezone ? (
              <div className="flex items-center gap-2">
                <Badge color="blue" className="min-w-0 flex-1 justify-start px-3 py-2 text-sm">
                  <span className="truncate">{getTimezoneLabel(secondaryTimezone)}</span>
                </Badge>
                <Button outline color="red" onClick={() => onSecondaryTimezoneChange('')}>
                  Remove
                </Button>
              </div>
            ) : null}

            <Input
              type="text"
              value={tzSearch}
              onChange={(e) => setTzSearch(e.target.value)}
              placeholder="Search timezones"
            />
            <div className="max-h-[180px] overflow-y-auto rounded-xl border border-zinc-200 bg-white p-1">
              {filteredTimezones.map(tz => (
                <button
                  key={tz}
                  type="button"
                  onClick={() => {
                    onSecondaryTimezoneChange(tz);
                    setTzSearch('');
                  }}
                  className={`block w-full rounded-lg px-3 py-2 text-left text-sm transition ${
                    secondaryTimezone === tz
                      ? 'bg-zinc-900 font-medium text-white'
                      : 'text-zinc-700 hover:bg-zinc-100'
                  }`}
                >
                  {getTimezoneLabel(tz)}
                </button>
              ))}
              {filteredTimezones.length === 0 && (
                <div className="px-3 py-4 text-center text-sm text-zinc-400">No matching timezones</div>
              )}
            </div>
          </section>

          {onQuickCreateEnabledChange && (
            <section className="space-y-3">
              <Subheading className="flex items-center gap-2 text-base">
                <Zap className="h-4 w-4 text-zinc-500" />
                Quick Event Creation
              </Subheading>
              <div
                role="button"
                tabIndex={0}
                className="flex w-full cursor-pointer items-center justify-between gap-4 rounded-xl border border-zinc-200 bg-white p-4 text-left transition hover:bg-zinc-50"
                onClick={() => onQuickCreateEnabledChange(!quickCreateEnabled)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onQuickCreateEnabledChange(!quickCreateEnabled);
                  }
                }}
              >
                <span className="text-sm font-medium text-zinc-950">Enable quick create</span>
                <Switch
                  checked={quickCreateEnabled}
                  color="dark/zinc"
                  onChange={onQuickCreateEnabledChange}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </section>
          )}

          <section className="space-y-3">
            <Subheading className="flex items-center gap-2 text-base">
              <CalendarIcon className="h-4 w-4 text-zinc-500" />
              Calendars
            </Subheading>

            {!googleConnected ? (
              <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-8 text-center text-sm text-zinc-500">
                Connect Google to choose calendars.
              </div>
            ) : loading ? (
              <div className="rounded-xl border border-zinc-200 bg-white px-4 py-8 text-center text-sm text-zinc-500">
                Loading calendars...
              </div>
            ) : calendars.length === 0 ? (
              <div className="rounded-xl border border-zinc-200 bg-white px-4 py-8 text-center text-sm text-zinc-500">
                No calendars found
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white">
                {calendars.map((calendar, index) => (
                  <div key={calendar.id}>
                    <button
                      type="button"
                      className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-zinc-50"
                      onClick={() => handleToggleCalendar(calendar.id)}
                    >
                      <Checkbox
                        checked={!hiddenCalendars.has(calendar.id)}
                        onChange={() => handleToggleCalendar(calendar.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span
                        className="h-4 w-4 rounded"
                        style={{ backgroundColor: calendar.backgroundColor || '#2563eb' }}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2 text-sm font-medium text-zinc-950">
                          <span className="truncate">{calendar.summary}</span>
                          {calendar.primary && <Badge color="zinc">Primary</Badge>}
                        </span>
                        {calendar.description && (
                          <span className="mt-0.5 block truncate text-xs text-zinc-500">
                            {calendar.description}
                          </span>
                        )}
                      </span>
                    </button>
                    {index < calendars.length - 1 && <Divider soft />}
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="space-y-3">
            <Subheading className="flex items-center gap-2 text-base">
              <Eye className="h-4 w-4 text-zinc-500" />
              Dim Events
            </Subheading>

            <div className="flex gap-2">
              <Input
                type="text"
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddKeyword()}
                placeholder="Keyword"
                className="flex-1"
              />
              <Button
                onClick={handleAddKeyword}
                disabled={!newKeyword.trim()}
              >
                Add
              </Button>
            </div>

            {dimKeywords.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {dimKeywords.map(keyword => (
                  <Badge key={keyword} color="zinc" className="gap-2 rounded-full px-3 py-1.5 text-sm">
                    {keyword}
                    <button
                      type="button"
                      onClick={() => handleRemoveKeyword(keyword)}
                      className="rounded-full text-zinc-500 transition hover:text-zinc-950"
                      aria-label={`Remove ${keyword}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </Badge>
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-zinc-200 px-4 py-4 text-center text-sm text-zinc-400">
                No keywords added yet
              </div>
            )}
          </section>
        </DialogBody>
      </div>
    </Dialog>
  );
}
